// src/contexts/AuthContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  getAuth, 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signOut, 
  sendPasswordResetEmail,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
  sendEmailVerification,
  verifyBeforeUpdateEmail
} from 'firebase/auth';
import { 
  doc, 
  getDoc, 
  setDoc,
  collection,
  query,
  where,
  getDocs,
  getFirestore 
} from 'firebase/firestore';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [currentCompany, setCurrentCompany] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [accessibleDepartmentIds, setAccessibleDepartmentIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const auth = getAuth();
  const db = getFirestore();

  /**
   * Determine user role based on authentication method.
   * Owner: User UID matches company ID (they created/own the company).
   * Manager: User has staff document with role="manager".
   */
  async function determineUserRole(user, companyId) {
    if (!user || !companyId) {
      setUserRole(null);
      setAccessibleDepartmentIds([]);
      return;
    }

    try {
      if (user.uid === companyId) {
        console.log('User is company OWNER');
        setUserRole('owner');
        setAccessibleDepartmentIds([]);
        return;
      }

      const staffQuery = query(
        collection(db, 'companies', companyId, 'staff'),
        where('email', '==', user.email)
      );
      
      const staffSnapshot = await getDocs(staffQuery);
      
      if (!staffSnapshot.empty) {
        const staffDoc = staffSnapshot.docs[0];
        const staffData = staffDoc.data();
        
        if (staffData.role === 'manager' || staffData.role === 'owner') {
          console.log('User is MANAGER with departments:', staffData.departmentIds);
          setUserRole('manager');
          setAccessibleDepartmentIds(staffData.departmentIds || []);
          
          setCurrentUser(prevUser => ({
            ...prevUser,
            staffId: staffDoc.id,
            departmentIds: staffData.departmentIds || [],
            staffRole: staffData.role
          }));
          return;
        }
      }

      console.log('No role found, defaulting to owner for email login');
      setUserRole('owner');
      setAccessibleDepartmentIds([]);
      
    } catch (error) {
      console.error('Error determining user role:', error);
      setUserRole('owner');
      setAccessibleDepartmentIds([]);
    }
  }

  /**
   * Fetch company data when user auth state changes.
   */
  async function fetchCompanyData(user) {
    if (!user) {
      setCurrentCompany(null);
      setUserRole(null);
      setAccessibleDepartmentIds([]);
      return;
    }
    
    try {
      const companyRef = doc(db, 'companies', user.uid);
      const companySnap = await getDoc(companyRef);
      
      if (companySnap.exists()) {
        const companyData = {
          id: companySnap.id,
          ...companySnap.data()
        };
        
        const settingsRef = doc(db, 'companies', user.uid, 'settings', 'companySettings');
        const settingsSnap = await getDoc(settingsRef);
        
        if (settingsSnap.exists()) {
          companyData.settings = settingsSnap.data();
        }
        
        setCurrentCompany(companyData);
        console.log('Company data loaded:', companyData);
        
        await determineUserRole(user, companyData.id);
        
      } else {
        console.log('No company data found for user:', user.uid);
        const defaultCompany = {
          id: user.uid,
          name: user.displayName || 'New Company',
          email: user.email
        };
        setCurrentCompany(defaultCompany);
        await determineUserRole(user, defaultCompany.id);
      }
    } catch (error) {
      console.error('Error fetching company data:', error);
      setUserRole('owner');
    }
  }

  // Auth state change handler
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      console.log('Auth state changed:', user?.email);
      setCurrentUser(user);
      
      if (user) {
        await fetchCompanyData(user);
      } else {
        setCurrentCompany(null);
        setUserRole(null);
        setAccessibleDepartmentIds([]);
      }
      
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  /**
   * Register a new company account.
   * Sends a verification email immediately after account creation so that
   * the email/password sign-in method is treated as verified by Firebase.
   * This prevents Firebase from removing the password when the user later
   * signs in via magic link.
   */
  async function register(email, password, companyName) {
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // Send verification email immediately so Firebase marks the account
      // as verified. Without this, using magic link sign-in later would
      // cause Firebase to remove the email/password sign-in method.
      await sendEmailVerification(user);

      const companyRef = doc(db, 'companies', user.uid);
      await setDoc(companyRef, {
        name: companyName,
        email: email,
        createdAt: new Date()
      });
      
      const settingsRef = doc(db, 'companies', user.uid, 'settings', 'companySettings');
      await setDoc(settingsRef, {
        name: companyName,
        allowPersonalDevices: true,
        requireLocationForClockIn: false,
        autoClockOutGlobal: false,
        breakSettings: {
          required: false,
          enforceComplianceRules: false,
          minimumDuration: 30,
          maximumDuration: 60,
          requiredDuration: 30,
          requireLocationForClockIn: false
        }
      });
      
      await fetchCompanyData(user);
      return user;
    } catch (error) {
      throw error;
    }
  }

  // Email and password login
  function login(email, password) {
    return signInWithEmailAndPassword(auth, email, password);
  }

  /**
   * Send a magic link to the provided email address.
   * The redirect URL uses window.location.origin so it works in both
   * local development and production without extra configuration.
   */
  function sendMagicLink(email) {
    const actionCodeSettings = {
      url: `${window.location.origin}/login`,
      handleCodeInApp: true,
    };
    return sendSignInLinkToEmail(auth, email, actionCodeSettings);
  }

  /**
   * Complete sign-in after the user clicks the magic link.
   * Validates that the current URL is a valid magic link before proceeding.
   */
  function completeMagicLinkSignIn(email) {
    if (!isSignInWithEmailLink(auth, window.location.href)) {
      return Promise.reject(new Error('auth/invalid-action-code'));
    }
    return signInWithEmailLink(auth, email, window.location.href);
  }

  /**
   * Check if the current URL contains a valid magic link.
   * Used on mount in CompanyLoginPage to detect magic link redirects.
   */
  function checkMagicLinkInUrl() {
    return isSignInWithEmailLink(auth, window.location.href);
  }

  // Logout
  function logout() {
    return signOut(auth);
  }
  
  // Send password reset email
  function resetPassword(email) {
    return sendPasswordResetEmail(auth, email);
  }

  /**
   * Send a verification link to a new email address to update the
   * signed-in user's email.
   *
   * Note: Firebase's modern client SDK requires email changes to be
   * confirmed via a link sent to the NEW address (verifyBeforeUpdateEmail).
   * The email in Firebase Auth does NOT change immediately after this
   * call resolves - it only changes once the user clicks the link in
   * their inbox. No password re-entry/re-authentication is required to
   * trigger this, though Firebase may still reject the request with
   * 'auth/requires-recent-login' if the current session is very old, in
   * which case the caller should ask the user to log out and back in.
   *
   * Known limitation: the company's Firestore email field (set at
   * registration) is not automatically kept in sync when the Auth email
   * changes this way, since the change happens asynchronously outside
   * the app (via the emailed link). If keeping that field in sync
   * becomes necessary, a Cloud Function listening for Auth email
   * changes would be the correct long-term solution.
   */
  function updateUserEmail(newEmail) {
    if (!auth.currentUser) {
      return Promise.reject(new Error('auth/no-current-user'));
    }
    return verifyBeforeUpdateEmail(auth.currentUser, newEmail);
  }

  const value = {
    currentUser,
    currentCompany,
    userRole,
    accessibleDepartmentIds,
    login,
    register,
    logout,
    resetPassword,
    updateUserEmail,
    sendMagicLink,
    completeMagicLinkSignIn,
    checkMagicLinkInUrl,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}